/**
 * WorkoutGroupField Component
 * Renders a workout group with its workouts using React Hook Form
 * Requirements: 1.3, 4.4, 6.1, 6.2
 */

import * as React from 'react';
import {memo, useCallback} from 'react';
import { Input as TextInput } from '../../../../../../components/ui';
import {Controller} from 'react-hook-form';
import {
  WorkoutGroupContainer,
  WorkoutGroupContainerHead,
  WorkoutGroupContainerMinified,
  WorkoutGroupContentEmpty,
  WorkoutToggleContainer,
} from '../../../../../components/workouts/WorkoutGroups.style';
import {WorkoutGroupMediaComponent} from '../../../../../components/workouts/WorkoutMediaComponent';
import {
  ButtonDeleteComponent,
  ButtonToggleContentClean,
  ButtonState,
} from '../../../../../components/button';
import {ButtonRowHeaderContainer} from '../../../../../components/button/Button.style';
import {TranslateMediaValueToFileProps} from '../../../../../../utils';
import {useWorkoutGroupField} from './WorkoutGroupField.use-case';
import {WorkoutField} from '../WorkoutField/WorkoutField';
import type {WorkoutGroupFieldProps} from './WorkoutGroupField.type';
import type {FileProps} from '../../../../../types';

/**
 * WorkoutGroupField Component
 * Manages a single workout group with its nested workouts
 * Uses React.memo for performance optimization (Requirement 6.2)
 */
export const WorkoutGroupField = memo(function WorkoutGroupField({
  form,
  groupIndex,
  onRemove,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  exerciseListDefault = [],
}: WorkoutGroupFieldProps): JSX.Element {
  const {
    workoutsField,
    isExpanded,
    toggleExpanded,
    addWorkout,
    removeWorkout,
    moveWorkout,
    handleMediaUpdate,
  } = useWorkoutGroupField(form, groupIndex);

  // Get current group values for media display
  const groupValues = form.watch(`workoutGroups.${groupIndex}`);
  const errors = form.formState.errors?.workoutGroups?.[groupIndex];

  // Toggle button state based on expanded state
  const buttonState = isExpanded ? ButtonState.open : ButtonState.closed;

  // Handle toggle content
  const handleToggleContent = useCallback(
    (value: ButtonState) => {
      if (
        (value === ButtonState.open && !isExpanded) ||
        (value === ButtonState.closed && isExpanded)
      ) {
        toggleExpanded();
      }
    },
    [isExpanded, toggleExpanded],
  );

  // Handle media update with proper typing
  const onMediaUpdate = useCallback(
    (
      mediaType:
        | 'image'
        | 'imageUrl'
        | 'video'
        | 'videoUrl'
        | 'audio'
        | 'audioUrl'
        | 'attachment'
        | 'attachmentUrl'
        | 'cover'
        | 'coverUrl',
      file: FileProps,
    ) => {
      const typeMap: Record<
        string,
        'cover' | 'video' | 'audio' | 'attachment'
      > = {
        cover: 'cover',
        coverUrl: 'cover',
        image: 'cover',
        imageUrl: 'cover',
        video: 'video',
        videoUrl: 'video',
        audio: 'audio',
        audioUrl: 'audio',
        attachment: 'attachment',
        attachmentUrl: 'attachment',
      };
      handleMediaUpdate(typeMap[mediaType], file);
    },
    [handleMediaUpdate],
  );

  // Filter out destroyed workouts for display
  const visibleWorkouts = workoutsField.fields.filter((field, index) => {
    const workout = form.getValues(
      `workoutGroups.${groupIndex}.workouts.${index}`,
    );
    return !workout?._destroy;
  });

  return (
    <WorkoutGroupContainer>
      <div className="WorkoutGroupContainer" style={{flex: 1}}>
        <WorkoutGroupContainerHead>
          <WorkoutToggleContainer>
            <i
              onClick={onMoveUp}
              className="far fa-long-arrow-up"
              style={{
                opacity: isFirst ? 0.3 : 1,
                cursor: isFirst ? 'not-allowed' : 'pointer',
              }}
            />
            <i
              onClick={onMoveDown}
              className="far fa-long-arrow-down"
              style={{
                opacity: isLast ? 0.3 : 1,
                cursor: isLast ? 'not-allowed' : 'pointer',
              }}
            />
          </WorkoutToggleContainer>

          {/* Name field with Controller for React Hook Form */}
          <Controller
            name={`workoutGroups.${groupIndex}.name`}
            control={form.control}
            render={({field, fieldState}) => (
              <TextInput
                s={12}
                id={`workoutGroup-${groupIndex}`}
                name={field.name}
                onChange={field.onChange}
                onBlur={field.onBlur}
                value={field.value || ''}
                placeholder="Escreva o nome do treino para criar um treino novo para esta rotina"
                inputClassName={fieldState.error ? 'invalid' : ''}
                error={fieldState.error?.message}
                label={null}
              />
            )}
          />

          <ButtonRowHeaderContainer>
            <ButtonToggleContentClean
              onPress={handleToggleContent}
              buttonState={buttonState}
              title="Minimize/Maximize o conteúdo para editar mais facilmente"
            />
            <ButtonDeleteComponent
              onPress={onRemove}
              name="Treino"
              title="Apagar o treino, suas etapas e exercício"
            />
          </ButtonRowHeaderContainer>
        </WorkoutGroupContainerHead>

        {isExpanded && (
          <div className="groupContent">
            <WorkoutGroupMediaComponent
              handleMediaUpdate={onMediaUpdate}
              image={TranslateMediaValueToFileProps(
                (groupValues?.coverUrl ||
                  groupValues?.cover) as unknown as FileProps,
              )}
              video={TranslateMediaValueToFileProps(
                (groupValues?.videoUrl ||
                  groupValues?.video) as unknown as FileProps,
                groupValues?.externalVideoUrl as unknown as any,
              )}
              audio={TranslateMediaValueToFileProps(
                (groupValues?.audioUrl ||
                  groupValues?.audio) as unknown as FileProps,
              )}
              attachment={TranslateMediaValueToFileProps(
                (groupValues?.attachmentUrl ||
                  groupValues?.attachment) as unknown as FileProps,
              )}
            />

            <div className="row">
              <div className="col s12">
                <div className="flow-text">Blocos do Treino</div>
              </div>
            </div>

            <div className="row">
              <div className="col s12">
                <ul>
                  {workoutsField.fields.map((field, workoutIndex) => {
                    const workout = form.getValues(
                      `workoutGroups.${groupIndex}.workouts.${workoutIndex}`,
                    );
                    // Skip destroyed workouts
                    if (workout?._destroy) return null;

                    const visibleIndex = visibleWorkouts.findIndex(
                      (w) => w.fieldId === field.fieldId,
                    );

                    return (
                      <li key={field.fieldId}>
                        <WorkoutField
                          form={form}
                          groupIndex={groupIndex}
                          workoutIndex={workoutIndex}
                          onRemove={() => removeWorkout(workoutIndex)}
                          onMoveUp={
                            visibleIndex > 0
                              ? () =>
                                  moveWorkout(workoutIndex, workoutIndex - 1)
                              : undefined
                          }
                          onMoveDown={
                            visibleIndex < visibleWorkouts.length - 1
                              ? () =>
                                  moveWorkout(workoutIndex, workoutIndex + 1)
                              : undefined
                          }
                          isFirst={visibleIndex === 0}
                          isLast={visibleIndex === visibleWorkouts.length - 1}
                          exerciseListDefault={exerciseListDefault}
                        />
                      </li>
                    );
                  })}
                </ul>

                {/* Add new workout button */}
                <WorkoutGroupContainer
                  className="clean-top add-new"
                  onClick={addWorkout}
                  style={{marginTop: '10px'}}>
                  <WorkoutGroupContentEmpty>
                    Adicionar novo bloco de treino
                  </WorkoutGroupContentEmpty>
                </WorkoutGroupContainer>
              </div>
            </div>
          </div>
        )}

        {!isExpanded && (
          <WorkoutGroupContainerMinified onClick={toggleExpanded}>
            <i className="far fa-ellipsis-h"></i>
          </WorkoutGroupContainerMinified>
        )}
      </div>
    </WorkoutGroupContainer>
  );
});

export default WorkoutGroupField;
